import React, { useState } from "react";

const BookingFooter = ({
  property,
}) => {

  /* =========================================================
     PROPERTY DATA
  ========================================================= */

  const propertyData =
    property?.client ||
    property?.data?.client ||
    property?.property ||
    property ||
    {};


  const propertyName =
    propertyData?.propertyName ||
    "Hotel";


  const propertyEmail =
    propertyData?.email ||
    "";


  const propertyMobile =
    propertyData?.mobile ||
    "";


  const adminEmail =
    propertyData?.adminEmail ||
    "";


  /* =========================================================
     ADDRESS
  ========================================================= */

  const propertyAddress = [
    propertyData?.address,
    propertyData?.city,
    propertyData?.state,
    propertyData?.country,
  ]
    .filter(Boolean)
    .join(", ");


  /* =========================================================
     CONTACT FORM
  ========================================================= */

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    mobile: "",
    note: "",
  });


  const [submitting, setSubmitting] =
    useState(false);


  const [message, setMessage] =
    useState("");


  /* =========================================================
     INPUT CHANGE
  ========================================================= */

  const handleChange = (event) => {

    const {
      name,
      value,
    } = event.target;


    setFormData((previous) => ({
      ...previous,
      [name]: value,
    }));

  };


  /* =========================================================
     SUBMIT ENQUIRY
  ========================================================= */

  const handleSubmit = async (event) => {

    event.preventDefault();

    setMessage("");


    if (!formData.name.trim()) {
      setMessage(
        "Please enter your name."
      );
      return;
    }


    if (!formData.email.trim()) {
      setMessage(
        "Please enter your email."
      );
      return;
    }


    if (!formData.mobile.trim()) {
      setMessage(
        "Please enter your mobile number."
      );
      return;
    }


    if (!formData.note.trim()) {
      setMessage(
        "Please enter your message."
      );
      return;
    }


    if (!adminEmail) {

      setMessage(
        "Unable to submit enquiry. Property email is not configured."
      );

      return;
    }


    try {

      setSubmitting(true);


      /*
       * IMPORTANT:
       * Replace this URL with your actual
       * public booking enquiry API endpoint.
       */

      const response = await fetch(
        "/api/guest/property-enquiry",
        {
          method: "POST",

          headers: {
            "Content-Type":
              "application/json",
          },

          body: JSON.stringify({

            tenantId:
              propertyData?.tenantId,

            propertyName,

            recipientEmail:
              adminEmail,

            name:
              formData.name.trim(),

            email:
              formData.email.trim(),

            mobile:
              formData.mobile.trim(),

            note:
              formData.note.trim(),

          }),
        }
      );


      const result =
        await response.json();


      if (!response.ok) {

        throw new Error(
          result?.message ||
          "Unable to submit enquiry."
        );

      }


      setMessage(
        "Thank you! Your enquiry has been submitted successfully."
      );


      setFormData({
        name: "",
        email: "",
        mobile: "",
        note: "",
      });


    } catch (error) {

      console.error(
        "PROPERTY ENQUIRY ERROR:",
        error
      );


      setMessage(
        error?.message ||
        "Something went wrong. Please try again."
      );

    } finally {

      setSubmitting(false);

    }

  };


  /* =========================================================
     RENDER
  ========================================================= */

  return (

    <footer id="contact" className="be-footer">

      <div className="be-footer-inner">


        {/* =================================================
            PROPERTY INFORMATION
        ================================================= */}

        <div className="be-footer-property">

          <h3>
            {propertyName}
          </h3>


          <span className="be-footer-property-type">
            HOTEL & RESORT
          </span>


          <p className="be-footer-description">
            Experience comfort,
            hospitality and
            unforgettable stays.
          </p>


          {/* ADDRESS */}

          {propertyAddress && (

            <div className="be-footer-contact">

              <span className="be-footer-contact-icon">
                📍
              </span>

              <span>
                {propertyAddress}
              </span>

            </div>

          )}


          {/* EMAIL */}

          {propertyEmail && (

            <div className="be-footer-contact">

              <span className="be-footer-contact-icon">
                ✉
              </span>

              <a
                href={`mailto:${propertyEmail}`}
              >
                {propertyEmail}
              </a>

            </div>

          )}


          {/* MOBILE */}

          {propertyMobile && (

            <div className="be-footer-contact">

              <span className="be-footer-contact-icon">
                ☎
              </span>

              <a
                href={`tel:${propertyMobile}`}
              >
                {propertyMobile}
              </a>

            </div>

          )}

        </div>


        {/* =================================================
            CONTACT / ENQUIRY FORM
        ================================================= */}

        <div className="be-footer-enquiry">

          <h3>
            Contact Us
          </h3>


          <p>
            Have a question or need
            more information?
            Send us an enquiry.
          </p>


          <form
            onSubmit={handleSubmit}
            className="be-footer-form"
          >


            {/* NAME */}

            <div className="be-footer-form-row">

              <div className="be-footer-form-group">

                <label>
                  Name
                </label>

                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="Your name"
                  autoComplete="name"
                />

              </div>


              {/* EMAIL */}

              <div className="be-footer-form-group">

                <label>
                  Email
                </label>

                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="Your email"
                  autoComplete="email"
                />

              </div>

            </div>


            {/* MOBILE */}

            <div className="be-footer-form-group">

              <label>
                Mobile
              </label>

              <input
                type="tel"
                name="mobile"
                value={formData.mobile}
                onChange={handleChange}
                placeholder="Your mobile number"
                autoComplete="tel"
              />

            </div>


            {/* NOTE */}

            <div className="be-footer-form-group">

              <label>
                Message
              </label>

              <textarea
                name="note"
                value={formData.note}
                onChange={handleChange}
                placeholder="How can we help you?"
                rows="4"
              />

            </div>


            {/* MESSAGE */}

            {message && (

              <div
                className={
                  message.includes(
                    "successfully"
                  )
                    ? "be-footer-form-success"
                    : "be-footer-form-error"
                }
              >
                {message}
              </div>

            )}


            {/* SUBMIT */}

            <button
              type="submit"
              className="be-footer-submit"
              disabled={submitting}
            >

              {submitting
                ? "Submitting..."
                : "SEND ENQUIRY"}

            </button>

          </form>

        </div>

      </div>


      {/* =================================================
          COPYRIGHT
      ================================================= */}

      <div className="be-footer-bottom">

        <span>
          © {new Date().getFullYear()}{" "}
          {propertyName}
        </span>

        <span>
          All rights reserved.
        </span>

      </div>

    </footer>

  );

};


export default BookingFooter;